import * as actions from 'client/actions/actionsManagement'
import { editorZindex } from 'client/constants/editorSettings'
import {
  deviceModes,
  useDeviceModeContext,
} from 'client/context/DeviceModeContext'
import { SidebarVisibilityActionsContext } from 'client/context/SidebarVisibilityContext'
import useManagement from 'client/hooks/useManagement'
import { selectors as managementSelectors } from 'client/reducers/managementReducer'
import { usePage } from 'client/store'
import { getPageType, isWebsitePageType } from 'client/store/page/pageSelectors'
import { pageTypes } from 'common/constants/pageTypes'
import { PageTypeEnum, websitePageTypes } from 'common/enums/PageTypeEnum'
import { useActions } from 'common/hooks/useActions'
import React, { useContext, useLayoutEffect } from 'react'
import { useWhiteLabelBrandSettings } from 'src/editor/hooks/use-white-label-brand'
import { Breadcrumbs } from 'src/editor/layout/breadcrumbs'
import { File04Icon } from 'src/editor/shared/icons/file-04-icon'
import { PaletteIcon } from 'src/editor/shared/icons/palette-icon'
import { Phone01Icon } from 'src/editor/shared/icons/phone-01-icon'
import { Settings02Icon } from 'src/editor/shared/icons/settings-02-icon'
import {
  ButtonSizeEnum,
  GhostButton,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import { SidebarTabEnum } from 'src/shared/enums/website-enums'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { isCustomDomain } from 'src/shared/utils/is-custom-domain'
import { twJoin } from 'tailwind-merge'
import { ExitPageButton } from './exit-page-button'
import { PreviewButton } from './preview-button'
import { SavePageButton } from './save-page-button'
import { TogglePopupButton } from './toggle-popup-button'
import { UndoRedo } from './undo-button'

const pagesWithoutPopupAbility = [
  pageTypes.inline,
  pageTypes.popup,
  pageTypes.lecture,
  pageTypes.customLogin,
  PageTypeEnum.NotFound,
]
const pageWithoutPagesSettings = [pageTypes.blogPostLayout, ...websitePageTypes]

export const Header = () => {
  const { t } = useTypedTranslation()
  const toggleActivePopup = useActions(actions.toggleActivePopup)
  const setActiveSidebarTab = useActions(actions.setActiveSidebarTab)
  const toggleSettings = useActions(actions.toggleSettings)
  const activeSidebarTab = useManagement(
    managementSelectors.getActiveSidebarTab,
  )

  const { brandSettings } = useWhiteLabelBrandSettings()
  const isWlUser = isCustomDomain()

  const isWebsitePage = usePage(isWebsitePageType)
  const pageType = usePage(getPageType)

  const sidebarActions = useContext(SidebarVisibilityActionsContext)

  const toggleSidebarTab = (sidebarTab: SidebarTabEnum) => {
    toggleActivePopup(null)
    toggleSettings(null)

    if (sidebarTab === activeSidebarTab) {
      setActiveSidebarTab(null)
    } else {
      setActiveSidebarTab(sidebarTab)
    }

    sidebarActions.showSidebar()
  }

  const { setDeviceMode, isMobile } = useDeviceModeContext()

  const toggleDeviceMode = () => {
    if (isMobile) {
      setDeviceMode(deviceModes.Desktop)
      return
    }
    setDeviceMode(deviceModes.Mobile)
  }

  useLayoutEffect(() => {
    if (isWebsitePage) {
      toggleSidebarTab(SidebarTabEnum.PagesManagement)
    }
  }, [isWebsitePage])

  return (
    <div
      className={twJoin(
        'relative col-start-1 col-end-3 flex justify-between border-gray',
        'border-border200 border-b bg-bgPrimaryBase px-7 py-1 text-sm',
        'font-medium text-textPrimary shadow-[0_2px_2px_0_rgba(46,81,98,0,18)]',
      )}
      style={{
        zIndex: editorZindex.header,
      }}
    >
      <div className="flex justify-between">
        <div className="inline-flex items-center">
          <img
            src={
              isWlUser
                ? brandSettings?.logo?.path
                : `${process.env.EDITOR_ASSETS_HOST}/assets/images/logo.png`
            }
            alt={
              isWlUser
                ? brandSettings?.logo?.name || 'logo'
                : 'systeme.io editor'
            }
            className="h-8"
            data-test-element="editor-logo"
          />
        </div>
        <div className="mr-1 flex gap-1 border-gray-150 border-r px-4">
          <UndoRedo />
        </div>
        <div className="flex gap-0.5">
          {isWebsitePage && (
            <GhostButton
              size={ButtonSizeEnum.Medium}
              active={activeSidebarTab === SidebarTabEnum.PagesManagement}
              onClick={() => toggleSidebarTab(SidebarTabEnum.PagesManagement)}
              title={t('core.header.pages.title')}
              iconBefore={<File04Icon title={t('core.header.pages.title')} />}
              className="[&>svg>path]:fill-textPrimary [&>svg>path]:stroke-none disabled:[&>svg>path]:fill-iconDisabled disabled:[&>svg>path]:stroke-none"
            >
              {t('core.header.pages.title')}
            </GhostButton>
          )}
          {!pagesWithoutPopupAbility.includes(pageType) && (
            <TogglePopupButton
              active={activeSidebarTab === SidebarTabEnum.PopupManagement}
              toggle={() => toggleSidebarTab(SidebarTabEnum.PopupManagement)}
            />
          )}
          {isWebsitePage && (
            <GhostButton
              size={ButtonSizeEnum.Medium}
              active={activeSidebarTab === SidebarTabEnum.WebsiteStylesSettings}
              onClick={() =>
                toggleSidebarTab(SidebarTabEnum.WebsiteStylesSettings)
              }
              title={t('core.header.style.title')}
              iconBefore={<PaletteIcon title={t('core.header.style.title')} />}
            >
              {t('core.header.website_styles_settings.label')}
            </GhostButton>
          )}
          {!pageWithoutPagesSettings.includes(pageType) && (
            <GhostButton
              size={ButtonSizeEnum.Medium}
              title={t('core.header.page_settings.title')}
              iconBefore={
                <Settings02Icon title={t('core.header.page_settings.title')} />
              }
              active={activeSidebarTab === SidebarTabEnum.PageEditing}
              onClick={() => toggleSidebarTab(SidebarTabEnum.PageEditing)}
              className="[&>svg>path]:fill-iconSecondary [&>svg>path]:stroke-none disabled:[&>svg>path]:fill-iconDisabled disabled:[&>svg>path]:stroke-none"
            >
              {t('core.header.page_settings.label')}
            </GhostButton>
          )}
          {isWebsitePage && (
            <GhostButton
              size={ButtonSizeEnum.Medium}
              title={t('core.header.website_settings.title')}
              iconBefore={
                <Settings02Icon
                  title={t('core.header.website_settings.title')}
                />
              }
              active={activeSidebarTab === SidebarTabEnum.WebsiteSettings}
              onClick={() => toggleSidebarTab(SidebarTabEnum.WebsiteSettings)}
              className="[&>svg>path]:fill-iconSecondary [&>svg>path]:stroke-none disabled:[&>svg>path]:fill-iconDisabled disabled:[&>svg>path]:stroke-none"
            >
              {t('core.header.website_settings.title')}
            </GhostButton>
          )}
        </div>
      </div>

      <div className="w-full flex-1 overflow-hidden">
        <Breadcrumbs />
      </div>

      <div className="flex justify-between gap-4">
        <div className="flex justify-between gap-1">
          <GhostIconButton
            onClick={toggleDeviceMode}
            title={t('core.header.device_appearance.mobile.title')}
            icon={
              <Phone01Icon
                title={t('core.header.device_appearance.mobile.title')}
              />
            }
            className="fill-iconSecondary stroke-none disabled:fill-iconDisabled disabled:stroke-none"
            active={isMobile}
          />
          <PreviewButton />
        </div>
        <div className="flex items-center gap-1.5">
          <SavePageButton />
          <ExitPageButton />
        </div>
      </div>
    </div>
  )
}
